import {
  ListBusinessUnitsForAccountApiArg,
  useListBusinessUnitsForAccountQuery,
} from "@generated";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import type { BusinessUnit } from "../types";

export interface HookArgs {
  page?: ListBusinessUnitsForAccountApiArg["page"];
  filter?: ListBusinessUnitsForAccountApiArg["filter"];
}

const useListBusinessUnits = ({ page, filter }: HookArgs) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { data, error, isLoading, refetch } =
    useListBusinessUnitsForAccountQuery({
      slug: accountSlug,
      page,
      filter,
    });

  let businessUnits: BusinessUnit[] = [];
  let customError: string | null = null;
  let pageMeta = {
    recordCount: 0,
    pageCount: 0,
  };

  if (data?.data) {
    try {
      businessUnits = data.data.map((bu) => {
        if (!bu.id) {
          throw new Error("Missing business unit id");
        }

        return {
          id: bu.id,
          name: bu.attributes?.name || "",
          externalName: bu.attributes?.["external-name"],
          city: bu.attributes?.city,
          state: bu.attributes?.state,
          active: true,
        };
      });

      if (!data.meta) {
        throw new Error("Missing data.meta");
      }

      if (!data.meta["record-count"]) {
        throw new Error('Missing data.meta["record-count"]');
      }

      if (!data.meta["page-count"]) {
        throw new Error('Missing data.meta["page-count"]');
      }
      pageMeta = {
        recordCount: data.meta["record-count"],
        pageCount: data.meta["page-count"],
      };
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }

  return {
    businessUnits,
    error: error || customError,
    isLoading,
    pageMeta,
    refetch,
  };
};

export default useListBusinessUnits;
